import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';

import { SidekickApiContext } from '../../contexts/SidekickApiContext';
import { clearFeedback, setFeedback } from '../../state/aiSidekickChatSlice';
import type { FeedbackPayload } from '../../state/chatTypes';

type Rating = FeedbackPayload['rating'];

/** Inputs identifying the assistant response a feedback row is bound to. */
export type UseMessageFeedbackSubmissionParams = {
  messageId: string;
  conversationId?: string;
  traceId?: string;
};

/** Orchestration callbacks returned to the feedback row. */
export type MessageFeedbackSubmission = {
  /**
   * Records a thumbs rating. Updates local state immediately so the
   * thumb fills without waiting on the network, then persists through
   * the host `submitFeedback` callback (no rationale). If the host
   * submission rejects, the selection is cleared back to neutral (no
   * thumb retained) and the promise rejects so the caller can surface
   * an error and collapse any open feedback form. Rows without a host
   * callback or trace id stay local-only, matching error-card feedback
   * behavior.
   */
  selectRating: (rating: Rating) => Promise<void>;
  /**
   * Persists a freeform comment alongside the existing rating. Awaits
   * the host submission and rethrows on failure so the inline form can
   * keep its draft open and surface the error toast; the local comment
   * is only stored once the host call resolves.
   */
  submitComment: (rating: Rating, comment: string) => Promise<void>;
};

/**
 * Bridges the message-feedback row's thumb and freeform interactions
 * to the host-supplied `submitFeedback` callback. Submission is keyed
 * by `traceId` (the backend's per-response key), so a thumb rating and
 * a later comment both post the same trace and the backend reconciles
 * them onto one record.
 */
const useMessageFeedbackSubmission = ({
  messageId,
  conversationId,
  traceId,
}: UseMessageFeedbackSubmissionParams): MessageFeedbackSubmission => {
  const dispatch = useDispatch();
  const submitFeedback = useContext(SidekickApiContext)?.submitFeedback;

  const selectRating = useCallback(
    async (rating: Rating) => {
      dispatch(setFeedback({ messageId, rating }));
      if (
        submitFeedback === undefined ||
        conversationId === undefined ||
        conversationId === '' ||
        traceId === undefined ||
        traceId === ''
      ) {
        return;
      }
      try {
        await submitFeedback({
          messageId,
          conversationId,
          traceId,
          rating,
        });
      } catch (error) {
        // A failed submission must not leave a thumb selected: clear
        // back to neutral so the filled thumb never implies a rating
        // that did not persist. Rethrow so the UI can show an error
        // and collapse any open feedback form.
        dispatch(clearFeedback({ messageId }));
        throw error;
      }
    },
    [dispatch, messageId, conversationId, traceId, submitFeedback]
  );

  const submitComment = useCallback(
    async (rating: Rating, comment: string) => {
      if (
        submitFeedback !== undefined &&
        conversationId !== undefined &&
        conversationId !== '' &&
        traceId !== undefined &&
        traceId !== ''
      ) {
        await submitFeedback({
          messageId,
          conversationId,
          traceId,
          rating,
          comment,
        });
      }
      dispatch(setFeedback({ messageId, rating, comment }));
    },
    [dispatch, messageId, conversationId, traceId, submitFeedback]
  );

  return { selectRating, submitComment };
};

export default useMessageFeedbackSubmission;
